import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Shuffle, Wand2, Mail, Phone, Building2, Link2, CalendarClock, Check } from "lucide-react";
import { useInquirySettings, PRIORITY_STYLES, KIND_STYLES, Pill, fmtDateTime, cap, notifyInquiriesChanged } from "@/lib/inquiry";
import { fmtDate } from "@/lib/format";
import { JourneyTimeline } from "@/components/inquiries/JourneyTimeline";
import { AddEventForm } from "@/components/inquiries/AddEventForm";
import { StatusDialog, ConvertDialog } from "@/components/inquiries/InquiryActions";
import { EditInquiryDialog } from "@/components/inquiries/EditInquiryDialog";

const Info = ({ label, children, testid }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-sm text-slate-900 mt-0.5" data-testid={testid}>{children || "—"}</div></div>
);

const ContactCard = ({ c, primary }) => (
  <div className="border border-slate-200 rounded-md p-3" data-testid={`inq-contact-${c.id}`}>
    <div className="text-sm font-medium">{c.name} {primary && <span className="text-[10px] text-amber-700 ml-1">PRIMARY</span>}</div>
    <div className="text-xs text-slate-500 capitalize">{[c.designation, c.type].filter(Boolean).join(" · ")}</div>
    {c.organisation && <div className="text-xs text-slate-600 mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{c.organisation}</div>}
    {c.email && <a href={`mailto:${c.email}`} className="text-xs text-blue-700 flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</a>}
    {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-blue-700 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</a>}
  </div>
);

const FollowupBox = ({ inq, onDone }) => (
  <div className={`rounded-md border p-3 ${inq.followup_overdue ? "border-rose-200 bg-rose-50" : "border-blue-100 bg-[#F5F9FF]"}`} data-testid="inq-followup-box">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" />Next follow-up</div>
    <div className={`text-sm font-semibold mt-0.5 ${inq.followup_overdue ? "text-rose-700" : "text-slate-900"}`}>{inq.next_followup_date ? fmtDate(inq.next_followup_date) : "None scheduled"}</div>
    {inq.next_action && <div className="text-xs text-slate-600">{inq.next_action}</div>}
    {inq.next_followup_date && <Button size="sm" variant="outline" data-testid="inq-followup-done" onClick={onDone} className="mt-2 h-7 gap-1 text-xs"><Check className="w-3 h-3" />Mark follow-up done</Button>}
  </div>
);

export default function InquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [settings] = useInquirySettings();
  const [inq, setInq] = useState(null);
  const [events, setEvents] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/inquiries/${id}`), api.get(`/inquiries/${id}/events`)]);
      setInq(a.data); setEvents(b.data);
    } catch (e) { if (e?.response?.status === 404) setMissing(true); else toast.error(formatErr(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (missing) return (
    <div className="text-sm text-slate-600" data-testid="inq-not-found">This inquiry doesn't exist or you don't have access to it.{" "}
      <button type="button" onClick={() => nav("/inquiries")} className="text-blue-700 hover:underline">Back to the register</button></div>
  );
  if (!inq || !settings) return <div className="text-sm text-slate-500">Loading…</div>;
  const saved = () => { setDlg(null); load(); };
  const clearFollowup = async () => {
    try { await api.post(`/inquiries/${id}/followup/clear`); toast.success("Follow-up marked done"); notifyInquiriesChanged(); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const fieldLabel = Object.fromEntries(settings.custom_fields.map((f) => [f.key, f.label]));
  return (
    <div data-testid="inquiry-detail">
      <button type="button" onClick={() => nav("/inquiries")} className="text-xs text-slate-500 hover:text-blue-700 inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3.5 h-3.5" />Inquiry register</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="mono text-sm text-blue-700" data-testid="inq-number">{inq.number}</span>
            <Pill className="bg-white border-slate-200 text-slate-600 capitalize" testid="inq-direction">{inq.direction}</Pill>
            <Pill className={KIND_STYLES[inq.status_kind]} testid="inq-status">{inq.status_label}</Pill>
            <Pill className={PRIORITY_STYLES[inq.priority]}>{cap(inq.priority)}</Pill>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-1" data-testid="inq-subject">{inq.subject}</h1>
          {inq.purpose && <p className="text-sm text-slate-500 mt-1">{inq.purpose}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="inq-edit-btn" onClick={() => setDlg("edit")} className="gap-1"><Pencil className="w-4 h-4" />Edit</Button>
          <Button variant="outline" data-testid="inq-status-btn" onClick={() => setDlg("status")} className="gap-1"><Shuffle className="w-4 h-4" />Status</Button>
          {settings.conversion_targets.length > 0 && inq.status_kind !== "closed" && <Button data-testid="inq-convert-btn" onClick={() => setDlg("convert")} className="bg-blue-700 hover:bg-blue-800 text-white gap-1"><Wand2 className="w-4 h-4" />{inq.links?.length ? "Convert again" : "Convert"}</Button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-5 min-w-0">
          {inq.status_kind === "closed" && (
            <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-sm" data-testid="inq-closure">Closed on {fmtDateTime(inq.closed_at)} · Reason: <b>{inq.closure_reason}</b>{inq.closure_note ? ` — ${inq.closure_note}` : ""}</div>
          )}
          <AddEventForm inquiry={inq} settings={settings} onAdded={load} correcting={correcting} onCancelCorrect={() => setCorrecting(null)} />
          <section className="bg-white border border-slate-200 rounded-md p-5">
            <h2 className="text-sm font-semibold mb-4">Journey <span className="text-slate-400 font-normal">· {events.length} entries, oldest first</span></h2>
            <JourneyTimeline events={events} onCorrect={(e) => { setCorrecting(e); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          </section>
        </div>
        <aside className="space-y-4">
          <FollowupBox inq={inq} onDone={clearFollowup} />
          {inq.links?.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3" data-testid="inq-links">
              <div className="text-[10px] uppercase tracking-wider text-emerald-800 mb-1">Converted into</div>
              {inq.links.map((l) => (
                <button type="button" key={l.id} data-testid={`inq-link-${l.id}`} onClick={() => nav(l.kind === "task" ? `/tasks/${l.id}` : `/obligations?q=${encodeURIComponent(l.label)}`)}
                  className="flex items-center gap-1 text-sm text-emerald-900 hover:underline"><Link2 className="w-3.5 h-3.5" /><span className="capitalize">{l.kind}</span>: {l.label}</button>
              ))}
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-2 gap-3">
            <Info label="Inquiry date">{fmtDate(inq.inquiry_date)}</Info>
            <Info label="Channel">{inq.channel}</Info>
            <Info label="Category">{inq.category}</Info>
            <Info label="Assigned to" testid="inq-assigned">{inq.assigned_name}</Info>
            <Info label="Party organisation">{inq.organisation}</Info>
            <Info label="Our entity">{inq.entity}</Info>
            <Info label="Registered by">{inq.created_by_name}</Info>
            <Info label="Last activity">{fmtDateTime(inq.last_activity_at)}</Info>
            {Object.entries(inq.custom || {}).map(([k, v]) => <Info key={k} label={fieldLabel[k] || k}>{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</Info>)}
            {inq.description && <div className="col-span-2"><Info label="Description"><span className="whitespace-pre-wrap">{inq.description}</span></Info></div>}
          </div>
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Contacts</div>
            {inq.contacts.length ? inq.contacts.map((c) => <ContactCard key={c.id} c={c} primary={c.id === inq.primary_contact_id} />) : <div className="text-xs text-slate-500">No contacts linked.</div>}
          </div>
        </aside>
      </div>
      <EditInquiryDialog open={dlg === "edit"} inquiry={inq} settings={settings} onClose={() => setDlg(null)} onSaved={saved} />
      <StatusDialog open={dlg === "status"} inquiry={inq} settings={settings} onClose={() => setDlg(null)} onSaved={saved} />
      <ConvertDialog open={dlg === "convert"} inquiry={inq} settings={settings} onClose={() => setDlg(null)} onSaved={saved} />
    </div>
  );
}
